import type {
  AdjustStockInput,
  AllocatePaymentInput,
  ConvertQuoteInput,
  CreateCustomerInput,
  CreateDistributorInput,
  CreateInvoiceFromOrderInput,
  CreateOrderInput,
  CreatePaymentInput,
  CreateProductInput,
  CreateQuoteInput,
  CreateSkuInput,
  ConfirmDocumentInput,
  Customer,
  DocumentRecord,
  CreateBrandInput,
  CreateCategoryInput,
  CreatePromotionInput,
  ExchangeRateHistoryEntry,
  ExchangeRateSetting,
  Promotion,
  ReviewDocumentInput,
  SetExchangeRateInput,
  UpdateBrandInput,
  UpdateCategoryInput,
  UpdatePromotionInput,
  Distributor,
  InventoryListItem,
  Invoice,
  InvoiceStatus,
  ListQuery,
  ListResult,
  MarkNotificationsReadInput,
  Notification,
  Order,
  OrderView,
  WarehouseOrderItem,
  OrderDecisionInput,
  OrderItem,
  OrderStatus,
  Payment,
  PaymentDecisionInput,
  PaymentStatus,
  PresignUploadInput,
  Brand,
  CatalogProduct,
  Category,
  Product,
  Quote,
  QuoteDecisionInput,
  QuoteStatus,
  Sku,
  UpdateCustomerInput,
  AccountStatusActionInput,
  VoidInvoiceInput,
  UpdateDistributorInput,
  UpdateProductInput,
  UserProfile,
} from '@lombok/shared';
import { ApiClient } from './client';

/** Health probe payload. */
export interface HealthResult {
  status: 'ok' | string;
  time?: string;
}

/**
 * Result of POST /orders. The backend returns the persisted order id + status,
 * whether it `requiresApproval`, and (if so) the `approvalReasons`. A
 * customer-safe `availabilitySummary` label is reserved for when sourcing lands
 * (see examples/api-examples.md); treat it as optional today.
 */
export interface CreateOrderResult extends Pick<Order, 'orderId' | 'orderNumber' | 'status'> {
  requiresApproval: boolean;
  approvalReasons: string[];
  availabilitySummary?: string;
}

/** Balance after a manual stock movement (staff-only: exact quantities). */
export interface StockAdjustmentResult {
  skuId: string;
  locationId: string;
  onHandQty: number;
  availableQty: number;
}

/** A reservation row attached to an order (what stock it holds, and where). */
export interface OrderReservation {
  skuId: string;
  locationId: string;
  quantity: number;
}

/**
 * Result of GET /orders/{id}. Warehouse gets the order without prices (see WarehouseOrder), and
 * customers, distributors and sales reps get no reservations (where the stock is held).
 */
export interface OrderDetail {
  order: OrderView;
  items: OrderItem[] | WarehouseOrderItem[];
  reservations?: OrderReservation[];
  /** Name of the assigned sales rep: the id and the rep's record never leave the API. */
  salesRepName?: string;
  /** Present for warehouse, which cannot read customers. */
  customerName?: string;
}

/** Result of a status transition (submit / approve / reject / cancel). */
export interface OrderTransitionResult {
  orderId: string;
  status: OrderStatus;
}

/**
 * Filters for GET /orders. The list is always scoped to the caller server-side
 * (customer → own, sales_rep → assigned, admin/finance → all). For admin/finance
 * the backend REQUIRES at least one of `status` / `customerId`. (A type alias —
 * not an interface — so it satisfies `Record<string, unknown>` query-key params.)
 */
export type OrdersQuery = {
  status?: OrderStatus;
  customerId?: string;
  cursor?: string;
  limit?: number;
};

/**
 * Filters for GET /quotes. The list is always scoped to the caller server-side
 * (customer → own, sales_rep → assigned, admin/finance → all). For admin/finance
 * the backend REQUIRES at least one of `status` / `customerId`. (A type alias —
 * not an interface — so it satisfies `Record<string, unknown>` query-key params.)
 */
export type QuotesQuery = {
  status?: QuoteStatus;
  customerId?: string;
  cursor?: string;
  limit?: number;
};

/**
 * Result of POST /quotes/{id}/convert — the source quote id, the id of the order
 * the conversion created, and the quote's resulting status (`converted`). The
 * order locks its exchange rate at conversion time and reserves stock; the
 * backend re-checks the quote is convertible and stays the authority.
 */
export interface ConvertQuoteResult {
  quoteId: string;
  orderId: string;
  status: QuoteStatus;
}

/** Result of presignUpload: a short-lived PUT URL plus the final S3 key + document id. */
export interface PresignUploadResult {
  uploadUrl: string;
  s3Key: string;
  /** Server-generated id for the file; pass to `confirmDocument` after the PUT. */
  documentId: string;
  /** Optional extra headers the client must echo on the PUT (e.g. content-type). */
  headers?: Record<string, string>;
  expiresInSeconds?: number;
}

/** Convert a shared ListQuery into a plain query-param record. */
function listParams(query?: ListQuery & { scope?: 'active' | 'all' }): Record<string, string | number | undefined> {
  if (!query) return {};
  return { cursor: query.cursor, limit: query.limit, q: query.q, scope: query.scope };
}

/**
 * Optional filters for GET /inventory. `locationId` scopes to one location;
 * `lowStock` asks the backend for balances at/under their reorder point. This is
 * admin/warehouse exact-quantity data — never customer-facing. (A type alias,
 * not an interface, so it satisfies `Record<string, unknown>` query-key params.)
 */
export type InventoryQuery = {
  locationId?: string;
  lowStock?: boolean;
  /** Page size (the API caps it at 100; it defaults to 25). */
  limit?: number;
  cursor?: string;
};

/**
 * Filters for GET /payments. The list is scoped to the caller server-side
 * (customer/distributor → own account, sales_rep → assigned, finance/admin →
 * all). `status` narrows by review state; `accountId` to a single account.
 * (A type alias — not an interface — so it satisfies `Record<string, unknown>`.)
 */
export type PaymentsQuery = {
  status?: PaymentStatus;
  accountId?: string;
  cursor?: string;
  limit?: number;
};

/**
 * GET /invoices/{id}/pdf: a short-lived download link when the PDF is ready, or `generating`
 * (HTTP 202) when it has been requested and is not there yet.
 */
export type InvoicePdfResult =
  | { status: 'ready'; url: string; fileName: string; expiresIn: number }
  | { status: 'generating' };

/**
 * Filters for GET /invoices. The list is scoped to the caller server-side
 * (customer/distributor → own account, finance/admin → all). For admin/finance
 * the backend REQUIRES at least one of `customerId` / `status` (an unfiltered
 * admin list is rejected). `overdue=true` narrows to past-due open invoices.
 * (A type alias — not an interface — so it satisfies `Record<string, unknown>`.)
 */
export type InvoicesQuery = {
  customerId?: string;
  status?: InvoiceStatus;
  overdue?: boolean;
  cursor?: string;
  limit?: number;
};

/**
 * Result of POST /payments/{id}/allocate — the payment id, the total amount
 * applied (integer minor units, in the payment's currency), and the invoices it
 * was applied to. The backend re-checks each invoice balance and the payment cap.
 */
export interface AllocatePaymentResult {
  paymentId: string;
  /** Total allocated, in integer minor units of the payment's currency. */
  allocated: number;
  invoiceIds: string[];
}

/**
 * Filters for GET /notifications (always the caller's own). `unread` limits to
 * not-yet-read items; `limit` caps the page. A type alias so it satisfies
 * `Record<string, unknown>` query-key params.
 */
export type NotificationsQuery = {
  unread?: boolean;
  limit?: number;
  cursor?: string;
};

/**
 * Result of GET /notifications: the page of notifications plus the caller's
 * total unread count (so the bell badge is exact even when the page is capped).
 */
export interface ListNotificationsResult extends ListResult<Notification> {
  unreadCount: number;
}

/** Result of POST /notifications/read — how many rows were marked read. */
export interface MarkNotificationsReadResult {
  updated: number;
}

/* --------------------------------- Reports -------------------------------- */

/**
 * AR aging bucket key, by whole days past an invoice's due date:
 *   `current` (not yet due / due today), `d1_30`, `d31_60`, `d60_plus`.
 * Mirrors the backend `agingBucket` classifier exactly.
 */
export type AgingBucket = 'current' | 'd1_30' | 'd31_60' | 'd60_plus';

/** Σ open balance per age bucket — integer minor USD. */
export interface AgingBuckets {
  current: number;
  d1_30: number;
  d31_60: number;
  d60_plus: number;
}

/**
 * GET /reports/dashboard payload (finance/admin only — others get 403). A capped,
 * read-only snapshot: AR aging totals/buckets, pending-payments count+sum,
 * open-orders-by-status counts, and a low-stock count. All amounts are integer
 * minor USD. `orders.byStatus` always carries the dashboard status set
 * (submitted, pending_approval, approved, dispatched, delivered, cancelled),
 * 0 when empty, so the UI row set is stable.
 */
export interface DashboardReport {
  ar: {
    totalOpenMinorUsd: number;
    openInvoiceCount: number;
    buckets: AgingBuckets;
  };
  payments: { pendingCount: number; pendingMinorUsd: number };
  orders: { byStatus: Record<string, number> };
  inventory: { lowStockCount: number };
  /** Server time the snapshot was composed (ISO 8601). */
  generatedAt: string;
}

/** One invoice row in the AR aging detail (each with computed `ageDays` + `bucket`). */
export interface ArAgingItem {
  invoiceId: string;
  /** Readable number (FAC-000123); absent on invoices created before numbering. */
  invoiceNumber?: string;
  customerId: string;
  dueDate: string;
  /** Open balance, integer minor USD. */
  balanceMinorUsd: number;
  status: string;
  /** Whole days past due (≤ 0 when not yet due → `current`). */
  ageDays: number;
  bucket: AgingBucket;
}

/**
 * GET /reports/ar-aging payload (finance/admin only). The same open-balance total
 * + age buckets as the dashboard tile, plus the per-invoice detail. Amounts are
 * integer minor USD. No query params.
 */
export interface ArAgingReport {
  totalOpenMinorUsd: number;
  buckets: AgingBuckets;
  items: ArAgingItem[];
}

/**
 * Typed endpoint surface. Each method maps 1:1 to a backend route, takes input
 * types from @lombok/shared schemas, and returns the unwrapped `data` payload
 * (the underlying client throws `ApiError` on failure). Read endpoints that
 * paginate return `ListResult<T>`.
 */
export function createEndpoints(client: ApiClient) {
  return {
    /** GET /health — unauthenticated readiness probe. */
    health: () => client.get<HealthResult>('/health'),

    /** GET /me — the authenticated user's profile (role, links, locale). */
    me: () => client.get<UserProfile>('/me'),

    /** GET /products — catalog list (role-scoped server-side). */
    listProducts: (query?: ListQuery & { scope?: 'active' | 'all' }) =>
      client.get<ListResult<CatalogProduct>>('/products', { query: listParams(query) }),

    /** GET /brands — brands available for a product (selector). */
    listBrands: () => client.get<Brand[]>('/brands'),

    /** GET /categories — categories available for a product (selector). */
    listCategories: () => client.get<Category[]>('/categories'),

    /** DELETE /products/{id} — only a product with no history (otherwise 409: archive it). */
    deleteProduct: (id: string) => client.delete<{ deleted: true }>(`/products/${id}`),

    /** DELETE /promotions/{id} — not while it is live (409). */
    deletePromotion: (id: string) => client.delete<{ deleted: true }>(`/promotions/${id}`),

    /** DELETE /brands/{id} — only when no product uses it. */
    deleteBrand: (id: string) => client.delete<{ deleted: true }>(`/brands/${id}`),

    /** DELETE /categories/{id} — only when no product uses it. */
    deleteCategory: (id: string) => client.delete<{ deleted: true }>(`/categories/${id}`),

    /** POST /brands — create a brand (admin). */
    createBrand: (input: CreateBrandInput) => client.post<{ brandId: string }>('/brands', input),

    /** PATCH /brands/{id} — rename or (de)activate a brand (admin). */
    updateBrand: (id: string, input: UpdateBrandInput) => client.patch<Brand>(`/brands/${id}`, input),

    /** POST /categories — create a category (admin). */
    createCategory: (input: CreateCategoryInput) => client.post<{ categoryId: string }>('/categories', input),

    /** PATCH /categories/{id} — rename or (de)activate a category (admin). */
    updateCategory: (id: string, input: UpdateCategoryInput) =>
      client.patch<Category>(`/categories/${id}`, input),

    /** POST /products — create a catalog product (admin). */
    createProduct: (input: CreateProductInput) => client.post<Product>('/products', input),

    /** GET /customers — customers list (role-scoped server-side). */
    listCustomers: (query?: ListQuery) =>
      client.get<ListResult<Customer>>('/customers', { query: listParams(query) }),

    /** POST /customers — create a customer account (admin). */
    createCustomer: (input: CreateCustomerInput) => client.post<Customer>('/customers', input),

    /** GET /customers/{id} — a single customer (account, credit, balance). */
    getCustomer: (customerId: string) =>
      client.get<Customer>(`/customers/${encodeURIComponent(customerId)}`),

    /** PATCH /customers/{id} — partial update (terms, credit limit, links, …). */
    updateCustomer: (customerId: string, input: UpdateCustomerInput) =>
      client.patch<Customer>(`/customers/${encodeURIComponent(customerId)}`, input),

    /** POST /customers/{id}/status — approve, reject, hold, suspend, reactivate or reopen (admin). */
    changeCustomerStatus: (customerId: string, input: AccountStatusActionInput) =>
      client.post<Customer>(`/customers/${encodeURIComponent(customerId)}/status`, input),

    /** GET /products/{id} — a single catalog product. */
    getProduct: (productId: string) =>
      client.get<Product>(`/products/${encodeURIComponent(productId)}`),

    /** PATCH /products/{id} — partial update (name, description, active, …). */
    updateProduct: (productId: string, input: UpdateProductInput) =>
      client.patch<Product>(`/products/${encodeURIComponent(productId)}`, input),

    /** GET /products/{id}/skus — a product's sellable SKUs (code, unit, base price). */
    listSkus: (productId: string) =>
      client.get<Sku[]>(`/products/${encodeURIComponent(productId)}/skus`),

    /**
     * POST /products/{id}/skus — create a SKU under a product (admin). The body's
     * `productId` is validated against the path by the backend; pass them in sync.
     */
    createSku: (productId: string, input: CreateSkuInput) =>
      client.post<Sku>(`/products/${encodeURIComponent(productId)}/skus`, input),

    /** GET /distributors — distributors list (admin). */
    listDistributors: (query?: ListQuery) =>
      client.get<ListResult<Distributor>>('/distributors', { query: listParams(query) }),

    /** POST /distributors — create a distributor account. */
    createDistributor: (input: CreateDistributorInput) =>
      client.post<Distributor>('/distributors', input),

    /** GET /distributors/{id} — a single distributor (territories, locations). */
    getDistributor: (distributorId: string) =>
      client.get<Distributor>(`/distributors/${encodeURIComponent(distributorId)}`),

    /** PATCH /distributors/{id} — partial update. */
    updateDistributor: (distributorId: string, input: UpdateDistributorInput) =>
      client.patch<Distributor>(`/distributors/${encodeURIComponent(distributorId)}`, input),

    /** POST /distributors/{id}/status — same actions as for customers (admin). */
    changeDistributorStatus: (distributorId: string, input: AccountStatusActionInput) =>
      client.post<Distributor>(`/distributors/${encodeURIComponent(distributorId)}/status`, input),

    /**
     * GET /inventory — exact stock balances (admin/warehouse). Optional
     * `locationId` and `lowStock` filters. Customers never see this; they get the
     * AVAILABILITY_LABELS_ES labels instead.
     */
    /**
     * POST /inventory/adjustments — receive goods or correct a count (admin/warehouse).
     * Returns the balance after the movement; 409 when the stock changed concurrently.
     */
    adjustStock: (input: AdjustStockInput) =>
      client.post<StockAdjustmentResult>('/inventory/adjustments', input),

    listInventory: (query?: InventoryQuery) =>
      client.get<ListResult<InventoryListItem>>('/inventory', {
        query: {
          locationId: query?.locationId,
          lowStock: query?.lowStock,
          limit: query?.limit,
          cursor: query?.cursor,
        },
      }),

    /**
     * POST /orders — create an order. The exchange rate is locked server-side at
     * creation. Pass `input.idempotencyKey` to make retries safe.
     */
    createOrder: (input: CreateOrderInput) =>
      client.post<CreateOrderResult>('/orders', input, { idempotencyKey: input.idempotencyKey }),

    /**
     * GET /orders — list orders, scoped to the caller server-side. For admin/finance
     * a `status` OR `customerId` filter is required (the backend rejects an
     * unfiltered admin list). Returns `ListResult<Order>` (header rows only).
     */
    listOrders: (query?: OrdersQuery) =>
      client.get<ListResult<OrderView>>('/orders', {
        query: {
          status: query?.status,
          customerId: query?.customerId,
          cursor: query?.cursor,
          limit: query?.limit,
        },
      }),

    /** GET /orders/{id} — order header + items + reservations (access-checked). */
    getOrder: (orderId: string) =>
      client.get<OrderDetail>(`/orders/${encodeURIComponent(orderId)}`),

    /** POST /orders/{id}/submit — DRAFT → submitted | pending_approval. */
    submitOrder: (orderId: string) =>
      client.post<OrderTransitionResult>(`/orders/${encodeURIComponent(orderId)}/submit`),

    /** POST /orders/{id}/approve — approve a pending order (admin / sales_manager). */
    approveOrder: (orderId: string, input: OrderDecisionInput = {}) =>
      client.post<OrderTransitionResult>(`/orders/${encodeURIComponent(orderId)}/approve`, input, {
        idempotencyKey: input.idempotencyKey,
      }),

    /** POST /orders/{id}/reject — reject a pending order; releases reservations. */
    rejectOrder: (orderId: string, input: OrderDecisionInput = {}) =>
      client.post<OrderTransitionResult>(`/orders/${encodeURIComponent(orderId)}/reject`, input, {
        idempotencyKey: input.idempotencyKey,
      }),

    /** POST /orders/{id}/cancel — cancel an order; releases any reservations. */
    cancelOrder: (orderId: string, input: OrderDecisionInput = {}) =>
      client.post<OrderTransitionResult>(`/orders/${encodeURIComponent(orderId)}/cancel`, input, {
        idempotencyKey: input.idempotencyKey,
      }),

    /**
     * POST /orders/{id}/pick — start fulfillment (submitted | approved → picking).
     * Warehouse/admin only. Optional reason + idempotency key.
     */
    pickOrder: (orderId: string, input: OrderDecisionInput = {}) =>
      client.post<OrderTransitionResult>(`/orders/${encodeURIComponent(orderId)}/pick`, input, {
        idempotencyKey: input.idempotencyKey,
      }),

    /**
     * POST /orders/{id}/dispatch — dispatch the order; stock leaves the warehouse
     * (→ dispatched). Warehouse/admin only. Optional reason + idempotency key.
     */
    dispatchOrder: (orderId: string, input: OrderDecisionInput = {}) =>
      client.post<OrderTransitionResult>(`/orders/${encodeURIComponent(orderId)}/dispatch`, input, {
        idempotencyKey: input.idempotencyKey,
      }),

    /**
     * POST /orders/{id}/deliver — confirm delivery (dispatched → delivered).
     * Warehouse/admin/driver. Optional delivery note (reason) + idempotency key.
     */
    deliverOrder: (orderId: string, input: OrderDecisionInput = {}) =>
      client.post<OrderTransitionResult>(`/orders/${encodeURIComponent(orderId)}/deliver`, input, {
        idempotencyKey: input.idempotencyKey,
      }),

    /**
     * POST /quotes — create a quote (cotización). Priced like an order but reserves
     * NO stock and locks no rate (quotes are USD). Authorized like order create
     * (admin/sales scoped to their customers). Returns the persisted `Quote`.
     */
    createQuote: (input: CreateQuoteInput) => client.post<Quote>('/quotes', input),

    /**
     * GET /quotes — list quotes, scoped to the caller server-side (customer → own,
     * sales_rep → assigned, admin/finance → all). For admin/finance a `status` OR
     * `customerId` filter is required. Returns `ListResult<Quote>` (header rows).
     */
    listQuotes: (query?: QuotesQuery) =>
      client.get<ListResult<Quote>>('/quotes', {
        query: {
          status: query?.status,
          customerId: query?.customerId,
          cursor: query?.cursor,
          limit: query?.limit,
        },
      }),

    /**
     * GET /quotes/{id} — a single quote (tenant-owned, access-checked). Unlike
     * invoices/payments the partition is the tenant, so no `customerId` query is
     * needed.
     */
    getQuote: (quoteId: string) => client.get<Quote>(`/quotes/${encodeURIComponent(quoteId)}`),

    /** POST /quotes/{id}/send — DRAFT → sent. Optional reason. */
    sendQuote: (quoteId: string, input: QuoteDecisionInput = {}) =>
      client.post<Quote>(`/quotes/${encodeURIComponent(quoteId)}/send`, input, {
        idempotencyKey: input.idempotencyKey,
      }),

    /** POST /quotes/{id}/accept — mark a sent quote accepted. Optional reason. */
    acceptQuote: (quoteId: string, input: QuoteDecisionInput = {}) =>
      client.post<Quote>(`/quotes/${encodeURIComponent(quoteId)}/accept`, input, {
        idempotencyKey: input.idempotencyKey,
      }),

    /** POST /quotes/{id}/reject — mark a sent quote rejected. Optional reason. */
    rejectQuote: (quoteId: string, input: QuoteDecisionInput = {}) =>
      client.post<Quote>(`/quotes/${encodeURIComponent(quoteId)}/reject`, input, {
        idempotencyKey: input.idempotencyKey,
      }),

    /**
     * POST /quotes/{id}/convert — turn an accepted/sent quote into a real order.
     * The body carries the `exchangeRate` (VES per 1 USD) the new order locks at
     * conversion, plus an optional idempotency key (so a retry isn't a double
     * conversion). Returns `{ quoteId, orderId, status }`.
     */
    convertQuote: (quoteId: string, input: ConvertQuoteInput) =>
      client.post<ConvertQuoteResult>(`/quotes/${encodeURIComponent(quoteId)}/convert`, input, {
        idempotencyKey: input.idempotencyKey,
      }),

    /**
     * POST /payments — register a (usually manual/offline) payment for review.
     * The proof file is uploaded first via presignUpload + {@link putToPresignedUrl};
     * pass the returned `proofS3Key` here so the backend links the proof to the
     * payment (the wire body is `createPaymentSchema` + an optional `proofS3Key`).
     */
    createPayment: (input: CreatePaymentInput & { proofS3Key?: string }) =>
      client.post<Payment>('/payments', input, { idempotencyKey: input.idempotencyKey }),

    /**
     * POST /documents/presign-upload — get a presigned PUT URL for a
     * direct-to-S3 upload (payment proof, RIF, etc.). The browser then PUTs the
     * file to `uploadUrl` via {@link putToPresignedUrl}; secrets never touch the
     * frontend.
     */
    presignUpload: (input: PresignUploadInput) =>
      client.post<PresignUploadResult>('/documents/presign-upload', input),

    /**
     * GET /payments — list payments, scoped to the caller server-side. Optional
     * `status` (review state) and `accountId` filters. Returns `ListResult<Payment>`.
     */
    listPayments: (query?: PaymentsQuery) =>
      client.get<ListResult<Payment>>('/payments', {
        query: {
          status: query?.status,
          accountId: query?.accountId,
          cursor: query?.cursor,
          limit: query?.limit,
        },
      }),

    /**
     * GET /payments/{id}?accountId= — a single payment. The backend REQUIRES `accountId`
     * (it identifies the DynamoDB partition `CUSTOMER#<accountId>`); pass it from the
     * list row's `payment.accountId`.
     */
    getPayment: (paymentId: string, accountId: string) =>
      client.get<Payment>(`/payments/${encodeURIComponent(paymentId)}`, { query: { accountId } }),

    /**
     * POST /payments/{id}/approve — finance/admin approves a manual payment.
     * Optional reason + idempotency key (so a retry isn't a double-approval).
     */
    approvePayment: (paymentId: string, accountId: string, input: PaymentDecisionInput = {}) =>
      client.post<Payment>(`/payments/${encodeURIComponent(paymentId)}/approve`, input, {
        query: { accountId },
        idempotencyKey: input.idempotencyKey,
      }),

    /** POST /payments/{id}/reject — finance/admin rejects a manual payment. */
    rejectPayment: (paymentId: string, accountId: string, input: PaymentDecisionInput = {}) =>
      client.post<Payment>(`/payments/${encodeURIComponent(paymentId)}/reject`, input, {
        query: { accountId },
        idempotencyKey: input.idempotencyKey,
      }),

    /**
     * GET /invoices — list invoices, scoped to the caller server-side
     * (customer/distributor → own account, finance/admin → all). For admin/finance
     * the backend REQUIRES `customerId` OR `status`. `overdue=true` narrows to
     * past-due open invoices. Returns `ListResult<Invoice>`.
     */
    listInvoices: (query?: InvoicesQuery) =>
      client.get<ListResult<Invoice>>('/invoices', {
        query: {
          customerId: query?.customerId,
          status: query?.status,
          overdue: query?.overdue,
          cursor: query?.cursor,
          limit: query?.limit,
        },
      }),

    /**
     * GET /invoices/{id}?customerId= — a single invoice. The backend uses
     * `customerId` to locate the DynamoDB partition (same pattern as
     * `getPayment`'s `accountId`); pass it from the list row's `invoice.customerId`.
     */
    getInvoice: (invoiceId: string, customerId: string) =>
      client.get<Invoice>(`/invoices/${encodeURIComponent(invoiceId)}`, {
        query: { customerId },
      }),

    /**
     * GET /invoices/{id}/pdf?customerId= — presigned PDF download, or `generating` while the
     * PDF worker builds it (asking also requests the PDF when the invoice has none).
     */
    getInvoicePdf: (invoiceId: string, customerId: string) =>
      client.get<InvoicePdfResult>(`/invoices/${encodeURIComponent(invoiceId)}/pdf`, {
        query: { customerId },
      }),

    /**
     * POST /invoices — generate a billing document (proforma/invoice) from an
     * existing order (finance/admin only). Idempotent per order. Pass
     * `input.idempotencyKey` to make retries safe. Returns the created `Invoice`.
     */
    createInvoiceFromOrder: (input: CreateInvoiceFromOrderInput) =>
      client.post<Invoice>('/invoices', input, { idempotencyKey: input.idempotencyKey }),

    /**
     * POST /invoices/{id}/void — void an invoice with no payments applied (finance/admin).
     * The reason is required; `customerId` locates the invoice's partition.
     */
    voidInvoice: (invoiceId: string, input: VoidInvoiceInput) =>
      client.post<Invoice>(`/invoices/${encodeURIComponent(invoiceId)}/void`, input),

    /**
     * POST /payments/{id}/allocate?accountId= — apply an APPROVED payment across
     * one or more invoices (finance/admin only). `accountId` is the payment's
     * partition; the body is `allocatePaymentSchema` (allocation `amount`s are
     * decimals in the payment's currency). The backend re-checks each invoice
     * balance and the payment cap.
     */
    allocatePayment: (paymentId: string, accountId: string, input: AllocatePaymentInput) =>
      client.post<AllocatePaymentResult>(`/payments/${encodeURIComponent(paymentId)}/allocate`, input, {
        query: { accountId },
        idempotencyKey: input.idempotencyKey,
      }),

    /**
     * GET /notifications — the caller's in-app notifications + total unread
     * count. `unread` limits to not-yet-read; `limit` caps the page.
     */
    listNotifications: (query?: NotificationsQuery) =>
      client.get<ListNotificationsResult>('/notifications', {
        query: { unread: query?.unread, limit: query?.limit, cursor: query?.cursor },
      }),

    /**
     * POST /notifications/read — mark notifications read. Pass specific
     * `notificationIds`, or `all: true` to clear the caller's whole inbox.
     */
    markNotificationsRead: (input: MarkNotificationsReadInput) =>
      client.post<MarkNotificationsReadResult>('/notifications/read', input),

    /**
     * GET /reports/dashboard — finance/admin Reportes snapshot (AR aging totals,
     * pending payments, open-orders-by-status, low-stock count). Capped/approximate
     * by design. Non-finance roles get 403. No query params.
     */
    getReportsDashboard: () => client.get<DashboardReport>('/reports/dashboard'),

    /**
     * GET /reports/ar-aging — finance/admin AR aging detail (open-balance total +
     * age buckets + per-invoice items). Non-finance roles get 403. No query params.
     */
    getArAging: () => client.get<ArAgingReport>('/reports/ar-aging'),

    /**
     * GET /settings/exchange-rate — the active USD->VES rate (null if unset).
     * Open to any authenticated caller (prices show in VES off this rate).
     */
    getExchangeRate: () => client.get<ExchangeRateSetting | null>('/settings/exchange-rate'),

    /**
     * PUT /settings/exchange-rate — set the live rate (admin/finance; 403 otherwise).
     * Writes the active row + an immutable history entry + an audit log.
     */
    setExchangeRate: (input: SetExchangeRateInput) =>
      client.put<ExchangeRateSetting>('/settings/exchange-rate', input),

    /** GET /settings/exchange-rate/history — past rate changes, newest first (admin/finance). */
    getExchangeRateHistory: () =>
      client.get<ListResult<ExchangeRateHistoryEntry>>('/settings/exchange-rate/history'),

    /**
     * POST /documents — register a document after its file was PUT to S3 via
     * {@link presignUpload} + {@link putToPresignedUrl}. Persists a PENDING record.
     */
    confirmDocument: (input: ConfirmDocumentInput) => client.post<DocumentRecord>('/documents', input),

    /** GET /documents?accountId= — an account's documents, newest first. */
    listDocuments: (accountId: string) =>
      client.get<ListResult<DocumentRecord>>('/documents', { query: { accountId } }),

    /** GET /documents/review-queue?status= — documents in a review status across accounts (admin/finance). */
    getDocumentReviewQueue: (status = 'pending') =>
      client.get<ListResult<DocumentRecord>>('/documents/review-queue', { query: { status } }),

    /** GET /documents/{id}/download?accountId= — a short-lived presigned view URL. */
    getDocumentDownloadUrl: (accountId: string, documentId: string) =>
      client.get<{ url: string; fileName: string }>(`/documents/${documentId}/download`, {
        query: { accountId },
      }),

    /** POST /documents/{id}/review?accountId= — approve/reject a document (admin/finance). */
    reviewDocument: (accountId: string, documentId: string, input: ReviewDocumentInput) =>
      client.post<DocumentRecord>(`/documents/${documentId}/review`, input, {
        query: { accountId },
      }),

    /** GET /promotions — staff see all; customers/distributors see only live ones. */
    listPromotions: () => client.get<ListResult<Promotion>>('/promotions'),

    /** GET /promotions/{id} — a single promotion. */
    getPromotion: (id: string) => client.get<Promotion>(`/promotions/${id}`),

    /** POST /promotions — create a promotion (admin). */
    createPromotion: (input: CreatePromotionInput) =>
      client.post<{ promotionId: string }>('/promotions', input),

    /** PATCH /promotions/{id} — update a promotion (admin). */
    updatePromotion: (id: string, input: UpdatePromotionInput) =>
      client.patch<Promotion>(`/promotions/${id}`, input),
  };
}

export type LombokApi = ReturnType<typeof createEndpoints>;
